import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, Calendar, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const SeasonalSearchManager = () => {
  const [seasons, setSeasons] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);

  const [formData, setFormData] = useState({
    season_name: '',
    headline_text: '',
    description: '',
    selected_products: [],
    start_month: 1,
    start_day: 1,
    end_month: 1,
    end_day: 31,
    is_active: true
  });

  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  useEffect(() => {
    loadSeasons();
    loadProducts();
  }, []);

  const loadSeasons = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-search/admin/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSeasons(data);
      }
    } catch (error) {
      console.error('Error loading seasons:', error);
      toast.error('Fehler beim Laden der Saisons');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.season_name.trim() || !formData.headline_text.trim()) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      const url = editingSeason
        ? `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-search/admin/${editingSeason.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-search/admin/create`;

      const response = await fetch(url, {
        method: editingSeason ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingSeason ? 'Saison aktualisiert!' : 'Saison erstellt!');
        setShowModal(false);
        setEditingSeason(null);
        resetForm();
        loadSeasons();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving season:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (season) => {
    setEditingSeason(season);
    setFormData({
      season_name: season.season_name,
      headline_text: season.headline_text,
      description: season.description,
      selected_products: season.selected_products || [],
      start_month: season.start_month,
      start_day: season.start_day,
      end_month: season.end_month,
      end_day: season.end_day,
      is_active: season.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (seasonId) => {
    if (!window.confirm('Saison wirklich löschen?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-search/admin/${seasonId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Saison gelöscht!');
        loadSeasons();
      }
    } catch (error) {
      console.error('Error deleting season:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (seasonId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-search/admin/${seasonId}/toggle`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('Status geändert!');
        loadSeasons();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Fehler');
    }
  };

  const toggleProductSelection = (productId) => {
    const selected = formData.selected_products.includes(productId);
    if (selected) {
      setFormData({
        ...formData,
        selected_products: formData.selected_products.filter(id => id !== productId)
      });
    } else {
      setFormData({
        ...formData,
        selected_products: [...formData.selected_products, productId]
      });
    }
  };

  const resetForm = () => {
    setFormData({
      season_name: '',
      headline_text: '',
      description: '',
      selected_products: [],
      start_month: 1,
      start_day: 1,
      end_month: 1,
      end_day: 31,
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-[#D4AF37]" />
            Saison-Suche Manager
          </h1>
          <p className="text-gray-600">
            Verwalten Sie saisonale Highlights in der Suchfunktion
          </p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Neue Saison
        </Button>
      </div>

      {/* Info Card */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-900">
            ℹ️ <strong>Automatische Saisonerkennung:</strong> Das System wählt automatisch die passende Saison basierend auf dem aktuellen Datum aus. Die ausgewählten Produkte und Texte werden dann in der mobilen Suchfunktion angezeigt.
          </p>
        </CardContent>
      </Card>

      {/* Seasons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {seasons.map((season) => (
          <Card key={season.id} className={season.is_current ? 'border-2 border-[#D4AF37] bg-[#FFF8DC]' : ''}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg mb-1">{season.season_name}</h3>
                  <div className="flex gap-2">
                    <Badge variant={season.is_active ? 'default' : 'secondary'}>
                      {season.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    {season.is_current && (
                      <Badge className="bg-[#D4AF37] text-white">
                        <Calendar className="h-3 w-3 mr-1" />
                        Aktuell
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm font-semibold text-[#8B7355] mb-2">
                {season.headline_text}
              </p>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {season.description}
              </p>

              <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <Calendar className="h-4 w-4" />
                <span>
                  {season.start_day}.{season.start_month}. - {season.end_day}.{season.end_month}.
                </span>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                {season.selected_products?.length || 0} Produkte ausgewählt
              </p>

              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(season)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleActive(season.id)}>
                  {season.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(season.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold">
                {editingSeason ? 'Saison bearbeiten' : 'Neue Saison'}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Saison-Name *</Label>
                  <Input
                    value={formData.season_name}
                    onChange={(e) => setFormData({ ...formData, season_name: e.target.value })}
                    placeholder="z.B. Weihnachten"
                  />
                </div>

                <div>
                  <Label>Überschrift *</Label>
                  <Input
                    value={formData.headline_text}
                    onChange={(e) => setFormData({ ...formData, headline_text: e.target.value })}
                    placeholder="z.B. 🎄 Weihnachtszauber bei Homeyoursoul"
                  />
                </div>

                <div>
                  <Label>Beschreibung</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Beschreibung der Saison..."
                    rows={3}
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start-Datum</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.start_day}
                        onChange={(e) => setFormData({ ...formData, start_day: parseInt(e.target.value) })}
                        placeholder="Tag"
                      />
                      <select
                        value={formData.start_month}
                        onChange={(e) => setFormData({ ...formData, start_month: parseInt(e.target.value) })}
                        className="border rounded px-3 py-2 w-full"
                      >
                        {months.map((month, idx) => (
                          <option key={idx + 1} value={idx + 1}>{month}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>End-Datum</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.end_day}
                        onChange={(e) => setFormData({ ...formData, end_day: parseInt(e.target.value) })}
                        placeholder="Tag"
                      />
                      <select
                        value={formData.end_month}
                        onChange={(e) => setFormData({ ...formData, end_month: parseInt(e.target.value) })}
                        className="border rounded px-3 py-2 w-full"
                      >
                        {months.map((month, idx) => (
                          <option key={idx + 1} value={idx + 1}>{month}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Selection */}
              <div>
                <Label className="mb-3 block">Produkte auswählen</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-2 border rounded">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`cursor-pointer p-3 border rounded transition-all ${
                        formData.selected_products.includes(product.id)
                          ? 'border-[#D4AF37] bg-[#FFF8DC]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {product.images?.[0] && (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-24 object-cover rounded mb-2"
                        />
                      )}
                      <p className="text-sm font-medium truncate">{product.name}</p>
                      <p className="text-sm text-gray-600">{formatEUR(product.price)}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {formData.selected_products.length} Produkte ausgewählt
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={handleSubmit} className="flex-1">
                  {editingSeason ? 'Aktualisieren' : 'Erstellen'}
                </Button>
                <Button variant="outline" onClick={() => {
                  setShowModal(false);
                  setEditingSeason(null);
                  resetForm();
                }}>
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonalSearchManager;